import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
  Chip,
  Paper,
  Stack,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as FinalIcon,
  Rule as RuleIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import { Reorder } from "motion/react";
import { useFormAccordo } from "./ContextAccordo";
import WorkflowGraph from "./WorkflowGraph";
import AutocompleteSelect from "./lib/AutocompleteSelect";

const Workflow = () => {
  const { formState, dispatch } = useFormAccordo();
  const [editedConfig, setEditedConfig] = useState(
    formState.workflow || {
      workflowName: "Default Workflow",
      states: [],
      transitions: [],
      roles: ["Admin", "User"],
    },
  );

  const availableStatusDataset = [
    {
      label: "Bozza",
      value: "Bozza",
    },
    {
      label: "In Revisione",
      value: "In Revisione",
    },
    {
      label: "Approvato",
      value: "Approvato",
    },
    {
      label: "Rifiuto",
      value: "Rifiuto",
    },
    {
      label: "Annullato",
      value: "Annullato",
    },
    {
      label: "Scaduto",
      value: "Scaduto",
    },
    {
      label: "Archiviato",
      value: "Archiviato",
    },
  ];
  const availableRolesFromContext = formState.workflowDatasets?.ruoli || [
    { label: "Admin", value: "Admin" },
    { label: "User", value: "User" },
  ];

  // State for new items
  const [newState, setNewState] = useState({
    id: "",
    label: "",
    isFinal: false,
    order: 0,
  });
  const [newTransition, setNewTransition] = useState({
    from: "",
    action: "",
    to: "",
    requiredRoles: [],
    validationRules: "",
    endpoint: "",
  });
  const [newRole, setNewRole] = useState("");

  // Sync with context if changed externally
  useEffect(() => {
    if (formState.workflow) {
      setEditedConfig(formState.workflow);
    }
  }, [formState.workflow]);

  const handleSave = () => {
    dispatch({ type: "set_workflow", payload: editedConfig });
    console.log("Workflow saved:", editedConfig);
  };

  const addState = () => {
    if (newState.id) {
      // Check if already exists
      if (editedConfig.states.find((s) => s.id === newState.id)) {
        alert("Questo stato è già stato aggiunto al workflow.");
        return;
      }

      const selectedStatus = availableStatusDataset.find(
        (s) => s.value === newState.id,
      );
      const label = selectedStatus ? selectedStatus.label : newState.id;

      const nextOrder =
        editedConfig.states.length > 0
          ? Math.max(...editedConfig.states.map((s) => s.order)) + 1
          : 1;

      const updatedConfig = {
        ...editedConfig,
        states: [
          ...editedConfig.states,
          { ...newState, label, order: nextOrder, position: { x: 100 + (editedConfig.states.length % 3) * 200, y: 100 + Math.floor(editedConfig.states.length / 3) * 150 } },
        ],
      };
      setEditedConfig(updatedConfig);
      setNewState({ id: "", label: "", isFinal: false, order: 0 });
    }
  };

  const removeState = (id) => {
    const updatedConfig = {
      ...editedConfig,
      states: editedConfig.states.filter((s) => s.id !== id),
      transitions: editedConfig.transitions.filter(
        (t) => t.from !== id && t.to !== id,
      ),
    };
    setEditedConfig(updatedConfig);
  };

  const handleReorderStates = (newOrder) => {
    const updatedStates = newOrder.map((s, index) => ({
      ...s,
      order: index + 1,
    }));
    setEditedConfig({ ...editedConfig, states: updatedStates });
  };

  const addTransition = () => {
    if (newTransition.from && newTransition.action && newTransition.to) {
      const rules = newTransition.validationRules
        ? newTransition.validationRules
            .split(",")
            .map((r) => r.trim())
            .filter((r) => r)
        : [];

      const updatedConfig = {
        ...editedConfig,
        transitions: [
          ...editedConfig.transitions,
          {
            id: `t-${Date.now()}`,
            from: newTransition.from,
            action: newTransition.action,
            to: newTransition.to,
            requiredRoles: newTransition.requiredRoles,
            validationRules: rules,
            endpoint: newTransition.endpoint,
            curvature: 0.25,
          },
        ],
      };
      setEditedConfig(updatedConfig);
      setNewTransition({
        from: "",
        action: "",
        to: "",
        requiredRoles: [],
        validationRules: "",
        endpoint: "",
      });
    }
  };

  const removeTransition = (index) => {
    const newTransitions = [...editedConfig.transitions];
    newTransitions.splice(index, 1);
    setEditedConfig({
      ...editedConfig,
      transitions: newTransitions,
    });
  };

  const addRole = () => {
    if (newRole && !editedConfig.roles.includes(newRole)) {
      setEditedConfig({
        ...editedConfig,
        roles: [...editedConfig.roles, newRole],
      });
      setNewRole("");
    }
  };

  const removeRole = (role) => {
    setEditedConfig({
      ...editedConfig,
      roles: editedConfig.roles.filter((r) => r !== role),
      transitions: editedConfig.transitions.map((t) => ({
        ...t,
        requiredRoles: t.requiredRoles?.filter((r) => r !== role) || [],
      })),
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={4}>
        {/* General Info */}
        <Card
          sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}
        >
          <CardHeader title="Configurazione Generale Workflow" />
          <CardContent>
            <TextField
              fullWidth
              label="Nome Workflow"
              value={editedConfig.workflowName}
              onChange={(e) =>
                setEditedConfig({
                  ...editedConfig,
                  workflowName: e.target.value,
                })
              }
            />
          </CardContent>
        </Card>

        {/* Roles Management */}
        <Card
          sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}
        >
          <CardHeader title="Gestione Ruoli Utente" />
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                label="Nuovo Ruolo"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                size="small"
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addRole}
              >
                Aggiungi
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {editedConfig.roles.map((role) => (
                <Chip
                  key={role}
                  label={role}
                  onDelete={() => removeRole(role)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Workflow Configuration (States & Transitions) */}
        <Card
          sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}
        >
          <CardHeader
            title="Definizione Stati e Transizioni"
            subheader="Gestisci gli stati del workflow e le regole di passaggio"
          />
          <CardContent>
            <Grid container spacing={4}>
              {/* States Column */}
              <Grid item xs={12} md={5}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  1. Definizione Stati
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <AutocompleteSelect
                        label="ID Stato"
                        options={availableStatusDataset}
                        getLabel={(s) => `${s.label} (${s.value})`}
                        value={newState.id}
                        onChange={(value) =>
                          setNewState({ ...newState, id: value })
                        }
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newState.isFinal}
                            onChange={(e) =>
                              setNewState({
                                ...newState,
                                isFinal: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Finale"
                      />
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={addState}
                        size="small"
                      >
                        Aggiungi
                      </Button>
                    </Box>
                  </Stack>
                </Paper>

                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Reorder.Group
                    axis="y"
                    values={editedConfig.states}
                    onReorder={handleReorderStates}
                    style={{ listStyle: "none", padding: 0, margin: 0 }}
                  >
                    {editedConfig.states.map((state) => (
                      <Reorder.Item
                        key={state.id}
                        value={state}
                        style={{ cursor: "grab" }}
                      >
                        <ListItem divider>
                          <Box
                            sx={{
                              mr: 2,
                              color: "text.disabled",
                              display: "flex",
                            }}
                          >
                            <DragIcon />
                          </Box>
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Chip
                                  label={state.order}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {state.label}
                                </Typography>
                                {state.isFinal && (
                                  <Chip
                                    label="FINALE"
                                    size="small"
                                    color="success"
                                    icon={<FinalIcon />}
                                  />
                                )}
                              </Box>
                            }
                            secondary={`ID: ${state.id}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => removeState(state.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </Reorder.Item>
                    ))}
                  </Reorder.Group>
                  {editedConfig.states.length === 0 && (
                    <Box
                      sx={{ p: 4, textAlign: "center", color: "text.disabled" }}
                    >
                      Nessuno stato definito
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Transitions Column */}
              <Grid item xs={12} md={7}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  2. Azioni e Transizioni
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <AutocompleteSelect
                          label="Da Stato"
                          options={editedConfig.states}
                          getValue={(s) => s.id}
                          value={newTransition.from}
                          onChange={(value) =>
                            setNewTransition({
                              ...newTransition,
                              from: value,
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Azione"
                          placeholder="es. Approva"
                          value={newTransition.action}
                          onChange={(e) =>
                            setNewTransition({
                              ...newTransition,
                              action: e.target.value,
                            })
                          }
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <AutocompleteSelect
                          label="A Stato"
                          options={editedConfig.states}
                          getValue={(s) => s.id}
                          value={newTransition.to}
                          onChange={(value) =>
                            setNewTransition({
                              ...newTransition,
                              to: value,
                            })
                          }
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={5}>
                        <AutocompleteSelect
                          label="Ruoli"
                          multiple
                          ChipProps={{ size: "small" }}
                          options={editedConfig.roles}
                          value={newTransition.requiredRoles}
                          onChange={(value) =>
                            setNewTransition({
                              ...newTransition,
                              requiredRoles: value,
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={7}>
                        <TextField
                          fullWidth
                          label="Validazioni (csv)"
                          placeholder="es. check_auth, is_ready"
                          value={newTransition.validationRules}
                          onChange={(e) =>
                            setNewTransition({
                              ...newTransition,
                              validationRules: e.target.value,
                            })
                          }
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Endpoint API"
                          placeholder="es. /api/workflow/approve"
                          value={newTransition.endpoint}
                          onChange={(e) =>
                            setNewTransition({
                              ...newTransition,
                              endpoint: e.target.value,
                            })
                          }
                          size="small"
                        />
                      </Grid>
                    </Grid>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={addTransition}
                      size="small"
                    >
                      Aggiungi Transizione
                    </Button>
                  </Stack>
                </Paper>

                <List
                  sx={{
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  {editedConfig.transitions.map((t, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: "bold",
                                color: "text.secondary",
                              }}
                            >
                              {t.from}
                            </Typography>
                            <ArrowIcon
                              sx={{ fontSize: 14, color: "action.active" }}
                            />
                            <Chip
                              label={t.action}
                              size="small"
                              color="primary"
                              variant="filled"
                            />
                            <ArrowIcon
                              sx={{ fontSize: 14, color: "action.active" }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: "bold",
                                color: "text.secondary",
                              }}
                            >
                              {t.to}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box
                            sx={{
                              mt: 0.5,
                              display: "flex",
                              gap: 2,
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: t.requiredRoles?.length
                                  ? "secondary.main"
                                  : "text.disabled",
                              }}
                            >
                              Ruoli: {t.requiredRoles?.join(", ") || "Nessuno"}
                            </Typography>
                            {t.validationRules &&
                              t.validationRules.length > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    alignItems: "center",
                                  }}
                                >
                                  <RuleIcon
                                    sx={{ fontSize: 12, color: "info.main" }}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="info.main"
                                  >
                                    {t.validationRules.length} regole
                                  </Typography>
                                </Box>
                              )}
                            {t.endpoint && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontFamily: "monospace",
                                }}
                              >
                                API: {t.endpoint}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeTransition(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                  {editedConfig.transitions.length === 0 && (
                    <Box
                      sx={{ p: 4, textAlign: "center", color: "text.disabled" }}
                    >
                      Nessuna transizione definita
                    </Box>
                  )}
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Visual Graph Editor */}
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <CardHeader
            title="Editor Grafico Visuale"
            subheader="Le modifiche nel grafico (posizionamento, curvature) e nelle tabelle sono sincronizzate."
          />
          <CardContent sx={{ p: 0 }}>
            <WorkflowGraph
              config={editedConfig}
              onUpdateConfig={(newConfig) => setEditedConfig(newConfig)}
            />
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 4 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{
              px: 6,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Salva Configurazione Workflow
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default Workflow;
